/**
 * Everyone under one organization — on this deal and in the CRM.
 *
 * Owner, 25 Sep: clicking an organization on the Parties roster, the
 * relationship map or the CRM must show every party and person under it: who
 * is at the firm on this deal, every CRM contact recorded at it, and their
 * roles on this deal and on the others. One component for all three places,
 * reading `GET /crm/organizations/members`, so the answer cannot differ by
 * screen.
 *
 * The CRM half shows as an org chart (the firm on top, its offices and people
 * beneath) or as the detailed list, from the same single read. The chart is
 * the default once the CRM holds at least one person at the firm; an empty
 * firm opens on the list. Opened from a person's profile, that person is
 * marked in both views and their office is the one the chart opens.
 *
 * The CRM half needs CRM access. A reader without it still sees the deal's
 * own roster entries at the firm (`onDeal`), and is told the CRM half could
 * not be read rather than being shown an empty firm.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Building2, Loader2 } from 'lucide-react'

import { crmService } from '@/services/crm'
import OrganizationChart from './OrganizationChart'
import { readError } from './profileFacts'

type PeopleView = 'chart' | 'list'

export interface OrganizationDealEntry {
  id: string
  name: string
  contact?: string | null
  roles: string[]
}

/** List / Chart switch for the CRM half. */
function ViewToggle({ view, onChange }: { view: PeopleView; onChange: (next: PeopleView) => void }) {
  return (
    <div role="group" aria-label="People view" className="inline-flex shrink-0 overflow-hidden rounded border border-stone-800">
      {(['list', 'chart'] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={view === option}
          onClick={() => onChange(option)}
          // Padding, border and radius are forced: the Parties dialogs style every
          // `button` inside them, and this switch must read the same there.
          className={clsx(
            '!rounded-none !border-0 !px-2 !py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
            view === option ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300',
          )}
        >
          {option === 'chart' ? 'Chart' : 'List'}
        </button>
      ))}
    </div>
  )
}

export default function OrganizationPeople({
  name,
  loanId,
  onDeal = [],
  focusId,
  showName = true,
  onOpenContact,
  onOpenDeal,
}: {
  name: string
  /** The deal whose roles to read; omitted on the CRM screen. */
  loanId?: string | null
  /** The deal roster's own entries at this organization. */
  onDeal?: OrganizationDealEntry[]
  /** The person whose profile this sits on: marked, and their office opens. */
  focusId?: string | null
  /** Off where the surrounding card's title already names the firm. */
  showName?: boolean
  onOpenContact?: (contactId: string) => void
  onOpenDeal?: (dealId: string) => void
}) {
  const members = useQuery({
    queryKey: ['crm', 'organization-members', name, loanId ?? null],
    queryFn: () => crmService.organizationMembers(name, loanId),
    retry: false,
    staleTime: 30_000,
  })
  const deal = members.data?.onThisDeal?.length ? members.data.onThisDeal : onDeal
  const people = members.data?.people ?? []
  // The reader's own choice wins; until they make one the chart is the view
  // for a firm with people in the CRM and the list for one without.
  const [chosenView, setChosenView] = useState<PeopleView | null>(null)
  const view: PeopleView = chosenView ?? (people.length > 0 ? 'chart' : 'list')
  const isSelf = (id: string) => Boolean(focusId) && id === focusId

  return (
    <div className="org-people space-y-3 text-[11px]" aria-label={`People under ${name}`}>
      {showName ? (
        <div className="flex items-center gap-1.5 text-stone-300">
          <Building2 size={12} className="text-amber-400" aria-hidden />
          <span className="font-medium">{name}</span>
          {members.data?.records?.length ? (
            <span className="text-stone-500">
              · {members.data.records.length} CRM record{members.data.records.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
      ) : null}

      {loanId ? (
        <section>
          <h5 className="mb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-500">
            On this deal ({deal.length})
          </h5>
          {deal.length ? (
            <ul className="space-y-1">
              {deal.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-stone-200">
                    {entry.name}
                    {entry.contact && entry.contact !== entry.name ? (
                      <span className="text-stone-500"> · contact {entry.contact}</span>
                    ) : null}
                  </span>
                  <span className="text-amber-200/80">{entry.roles.join(' / ')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-stone-500">Nobody at this organization is on this deal's roster.</p>
          )}
        </section>
      ) : null}

      <section>
        <div className="mb-1 flex items-center justify-between gap-2">
          <h5 className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-500">
            In the CRM ({members.isSuccess ? people.length : '…'})
          </h5>
          {members.isSuccess ? <ViewToggle view={view} onChange={setChosenView} /> : null}
        </div>
        {members.isPending ? (
          <p className="flex items-center gap-1.5 text-stone-500" role="status">
            <Loader2 size={10} className="motion-safe:animate-spin" /> Reading the CRM…
          </p>
        ) : members.isError ? (
          <p className="text-stone-400" role="status">
            {readError(members.error, 'The CRM could not be read for this organization.')}
            {loanId ? ' The deal roster above is complete for this deal.' : ''}
          </p>
        ) : view === 'chart' ? (
          <OrganizationChart
            name={name}
            records={members.data.records ?? []}
            people={people}
            focusId={focusId}
            onOpenContact={onOpenContact}
          />
        ) : people.length === 0 ? (
          <p className="text-stone-500">No CRM contact is recorded at this organization.</p>
        ) : (
          <ul className="space-y-2">
            {people.map((person) => (
              <li
                key={person.id}
                aria-current={isSelf(person.id) ? 'true' : undefined}
                className={clsx(
                  'rounded border px-2 py-1.5',
                  isSelf(person.id) ? 'border-amber-400/60 bg-amber-400/[0.08]' : 'border-stone-800/80 bg-black/20',
                )}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  {isSelf(person.id) ? (
                    <span className="text-amber-100">
                      {person.name || person.email || person.id}
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.12em] text-amber-300/90">This contact</span>
                    </span>
                  ) : onOpenContact ? (
                    <button
                      type="button"
                      onClick={() => onOpenContact(person.id)}
                      className="text-left text-stone-100 underline decoration-dotted underline-offset-2 hover:text-amber-200"
                    >
                      {person.name || person.email || person.id}
                    </button>
                  ) : (
                    <a
                      href={`/crm?focus=${encodeURIComponent(person.id)}&open=1`}
                      className="text-stone-100 underline decoration-dotted underline-offset-2 hover:text-amber-200"
                    >
                      {person.name || person.email || person.id}
                    </a>
                  )}
                  {person.rolesOnThisDeal.length ? (
                    <span className="text-amber-200/80">On this deal: {person.rolesOnThisDeal.join(', ')}</span>
                  ) : null}
                </div>
                <div className="text-stone-500">
                  {[person.title, person.email, person.companyType].filter(Boolean).join(' · ') || 'No title or address on record'}
                </div>
                {person.office ? (
                  <div className="text-stone-500">
                    {person.office}
                    {person.officeBasis ? <span className="text-stone-600"> · {person.officeBasis}</span> : null}
                  </div>
                ) : null}
                <div className="text-[10px] text-stone-600">{person.basis}</div>
                {person.deals.length ? (
                  <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-stone-400">
                    {person.deals.map((d) => (
                      <li key={`${person.id}-${d.dealId}-${d.role ?? ''}`}>
                        {onOpenDeal ? (
                          <button
                            type="button"
                            onClick={() => onOpenDeal(d.dealId)}
                            className="underline decoration-dotted underline-offset-2 hover:text-amber-200"
                          >
                            {d.property || d.dealId}
                          </button>
                        ) : (
                          <a
                            href={`/pipeline?deal=${encodeURIComponent(d.dealId)}&tab=parties`}
                            className="underline decoration-dotted underline-offset-2 hover:text-amber-200"
                          >
                            {d.property || d.dealId}
                          </a>
                        )}
                        {d.role ? ` · ${d.role}` : ''}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/** The CRM profile's section: an organization's people, or a person's firm
 *  with that person marked. Open from the start: the org chart is what the
 *  profile promises "right off the bat" (CY-24, 26 Oct), and the full profile
 *  already reads the same firm the moment it opens. Closing it stops nothing
 *  already read; a caller that wants it shut passes `defaultOpen={false}`. */
export function OrganizationSection({
  title,
  name,
  defaultOpen = true,
  focusId,
  onOpenContact,
}: {
  title: string
  name: string
  defaultOpen?: boolean
  /** The person the profile is about, marked on the chart. */
  focusId?: string | null
  onOpenContact?: (contactId: string) => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <details
      className="org-section border-b border-amber-400/10 px-4 py-3"
      aria-label={title}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500 hover:text-stone-300">
        {title}
      </summary>
      {open ? (
        <div className="mt-2">
          {/* The section's title and the chart's own root already name the firm. */}
          <OrganizationPeople name={name} focusId={focusId} showName={false} onOpenContact={onOpenContact} />
        </div>
      ) : null}
    </details>
  )
}
